import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEdit, FaTrash, FaDownload } from 'react-icons/fa';
// import { API } from '../../../Services/axios';
// import { URLS } from '../../../config/urls.config';
// import { toast } from 'react-toastify';

const DemandesTable = ({ 
  demandes, 
  projects, 
  onDemandeSelect, 
  pagination, 
  onPageChange,
  onDemandeDelete 
}) => {
  const navigate = useNavigate();
  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date invalide';
    }
  };

  // Fonction pour obtenir le statut avec une couleur
  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { class: 'bg-warning', text: 'En attente' },
      'in_progress': { class: 'bg-info', text: 'En cours' },
      'completed': { class: 'bg-success', text: 'Terminé' },
      'cancelled': { class: 'bg-danger', text: 'Annulé' },
      'draft': { class: 'bg-secondary', text: 'Brouillon' }
    };

    const config = statusConfig[status] || { class: 'bg-secondary', text: status || 'Inconnu' };
    
    return (
      <span className={`badge ${config.class} text-white`}>
        {config.text}
      </span>
    );
  };

  // const handleDeleteDemande = (demandeId, event) => {
  //   event.preventDefault(); // Empêche la navigation si le bouton est dans un Link
  //   if (window.confirm("Êtes-vous sûr de vouloir supprimer ce projet ? Cette action est irréversible.")) {
  //     API.delete(URLS.BE.PROJECT.delete(demandeId))
  //       .then(() => {
  //         toast.success("Projet supprimé avec succès !");
  //         setProjectsTab(projectsTab.filter(p => p.id !== projectId));
  //       })
  //       .catch(error => {
  //         toast.error(error?.response?.data?.detail || "Erreur lors de la suppression du projet.");
  //       });
  //   }
  // };

  // Fonction pour gérer la pagination
  const renderPagination = () => {
    if (!pagination || pagination.count <= 10) return null;

    const totalPages = Math.ceil(pagination.count / 10);
    const currentPage = pagination.currentPage || 1;
    
    const pages = [];
    const maxVisiblePages = 5;
    
    // Calculer les pages à afficher
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Bouton précédent
    if (pagination.previous) {
      pages.push(
        <li key="prev" className="page-item">
          <button 
            className="page-link" 
            onClick={() => onPageChange(currentPage - 1)}
          >
            Précédent
          </button>
        </li>
      );
    }

    // Pages numérotées
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
          <button 
            className="page-link" 
            onClick={() => onPageChange(i)}
          >
            {i}
          </button>
        </li>
      );
    }

    // Bouton suivant
    if (pagination.next) {
      pages.push(
        <li key="next" className="page-item">
          <button 
            className="page-link" 
            onClick={() => onPageChange(currentPage + 1)}
          >
            Suivant
          </button>
        </li>
      );
    }

    return (
      <nav aria-label="Pagination des demandes">
        <ul className="pagination justify-content-center">
          {pages}
        </ul>
      </nav>
    );
  };

  if (!demandes || demandes.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="mb-3">
          <i className="fas fa-inbox fa-3x text-muted"></i>
        </div>
        <h5 className="text-muted">Aucune demande trouvée</h5>
        <p className="text-muted">Il n'y a actuellement aucune demande à afficher.</p>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover table-striped">
        <thead className="table-dark">
          <tr>
            {/* <th scope="col">ID</th> */}
            <th scope="col">Nom</th>
            <th scope="col">Email</th>
            <th scope="col">Projet</th>
            <th scope="col">Statut</th>
            <th scope="col">Date de création</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {demandes.map((demande) => (
            <tr key={demande.id}>
              {/* <td>
                <span className="badge bg-primary">#{demande.id}</span>
              </td> */}
              <td>
                <div>
                  <strong>{demande.name || 'N/A'}</strong>
                  {demande.numero_tel && (
                    <div className="text-muted small">
                      <i className="fas fa-phone me-1"></i>
                      {demande.numero_tel}
                    </div>
                  )}
                </div>
              </td>
              <td>
                <a href={`mailto:${demande.email}`} className="text-decoration-none">
                  {demande.email || 'N/A'}
                </a>
              </td>
              <td>
                {demande.projet ? (
                  <span className="badge bg-info">
                    {projects[demande.projet] || `Projet #${demande.projet}`}
                  </span>
                ) : (
                  <span className="text-muted">Aucun projet</span>
                )}
              </td>
              <td>
                {getStatusBadge(demande.status || demande.project_step)}
              </td>
              <td>
                <div className="small">
                  {formatDate(demande.created_at || demande.date_creation)}
                </div>
              </td>
              <td>
                <div className="btn-group" role="group">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => navigate(`/demande-details/${demande.id}`)}
                    title="Voir les détails"
                  >
                    <FaEye />
                  </button>
                  {/* <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      // Logique pour modifier la demande
                      console.log('Modifier demande:', demande.id);
                    }}
                    title="Modifier"
                  >
                    <FaEdit />
                  </button> */}
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => {
                      // Logique pour supprimer la demande
                      onDemandeDelete(demande.id);
                      console.log('Supprimer demande:', demande.id);
                    }}
                    title="Supprimer"
                  >
                    <FaTrash />
                  </button>
                  {/* {demande.documents && demande.documents.length > 0 && (
                    <button
                      className="btn btn-outline-success btn-sm"
                      onClick={() => {
                        // Logique pour télécharger les documents
                        console.log('Télécharger documents:', demande.id);
                      }}
                      title="Télécharger les documents"
                    >
                      <FaDownload />
                    </button>
                  )} */}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
};

export default DemandesTable;